import React, { useCallback, useState } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  CalendarDaysIcon,
  ChartPieIcon,
  EllipsisHorizontalIcon,
  PlusIcon,
  PrinterIcon,
  ServerStackIcon,
  ShareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { PDFIcon } from 'components/Icons/icons/PDF';
import { TransfertCJIcon } from 'components/Icons/icons/TransfertCJIcon';
import tw from 'twin.macro';
import Button from '@teetsh/app/src/components/Buttons/Button';
import Tooltip2 from '@teetsh/app/src/components/Tooltip2';
import { useTranslation } from 'react-i18next';
import { useEDTHistory } from '@teetsh/app/src/queries/edt/historyAtoms';
import useUndoEDT from '@teetsh/app/src/queries/edt/useUndoEDT';
import useRedoEDT from '@teetsh/app/src/queries/edt/useRedoEDT';
import useIsMobile from '@teetsh/app/src/components/ResponsiveSwitcher/useIsMobile';
import { Menu } from '@teetsh/app/src/components/Menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDownloadEDT } from '@teetsh/app/src/queries/edt/useDownloadEDT';
import { DateDialog } from '../../Sidebar/DateDialog';
import { useTransfertEDTEventsToCJ } from '@teetsh/app/src/queries/edt/useTransfertEDTEventsToCJ';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useOpenCreateEDTSeance } from '@teetsh/app/src/components/EDT/EventCreatorModal/atom';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import startOfMinute from '@teetsh/app/src/utils/time/startOfMinute';
import setHours from '@teetsh/app/src/utils/time/setHours';
import { v4 as uuid } from 'uuid';
import SimpleModal from '@teetsh/app/src/components/SimpleModal';
import useDeleteEDT from '@teetsh/app/src/queries/edt/useDeleteEDT';
import { trackEvent } from '@teetsh/app/src/queries/user/track';
import { format } from '@teetsh/app/src/utils/time/format';
import {
  isoDatePattern,
  isoDatePatternWithSlashes,
} from 'utils/time/datePatterns';
import { useAtom } from 'jotai';
import { sendEDTToCMSPayloadAtom } from '../../Sidebar/EDTSendToCMSModal/atom';
import startOfWeek from '@teetsh/app/src/utils/time/startOfWeek';
import endOfWeek from '@teetsh/app/src/utils/time/endOfWeek';
import addWeeks from '@teetsh/app/src/utils/time/addWeeks';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { getMutualWorkingDays } from '@teetsh/app/src/containers/utils';
import Cookies from 'js-cookie';
import qs from 'qs';
import { Classroom } from '@teetsh/app/src/types/classroom';

interface EdtToolbarActionsProps {
  edtId: string;
  isReadOnly?: boolean;
  onOpenDrawer?: (view: string) => void;
  displayedClassrooms?: string[];
  emploiDuTemps?: any;
  isGuest?: boolean;
  date: Date;
}

export function EdtToolbarActions({
  edtId,
  isReadOnly,
  onOpenDrawer,
  displayedClassrooms = [],
  emploiDuTemps,
  isGuest,
  date,
}: EdtToolbarActionsProps) {
  const { t } = useTranslation(['emploiDuTemps', 'common']);
  const isMobile = useIsMobile();
  const { isFutureEmpty, isPastEmpty } = useEDTHistory(edtId);
  const undo = useUndoEDT(edtId);
  const redo = useRedoEDT(edtId);
  const location = useLocation();
  const navigate = useNavigate();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { access, locale } = useUser();
  const classrooms = useClassrooms();
  const openCreateSeance = useOpenCreateEDTSeance();
  const [, setSendToCMSPayload] = useAtom(sendEDTToCMSPayloadAtom);

  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);
  const [isEmptyEDTModalOpen, setIsEmptyEDTModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false);

  const [transfert, isTransfering] = useTransfertEDTEventsToCJ();

  const { download: downloadEDTPDF, isDownloading } = useDownloadEDT();

  const deleteEDT = useDeleteEDT();

  const isSharable = qs.parse(location.search.substr(1))?.sharable;
  const shouldDisplayShare =
    Cookies.get('teetshadmin') || isSharable === 'true';

  const handleVolumeClick = () => {
    onOpenDrawer?.('volume');
  };

  const handlePDFClick = () => {
    navigate(
      `${location.pathname}/preview?classrooms=${displayedClassrooms.join(
        ',',
      )}`,
    );
  };

  const downloadPDF = useCallback(() => {
    if (!emploiDuTemps) return;

    return downloadEDTPDF({
      classrooms: displayedClassrooms,
      edtName: emploiDuTemps.name,
      edtId: emploiDuTemps.id,
      callback: () => setIsEmptyEDTModalOpen(false),
    });
  }, [
    emploiDuTemps?.name,
    emploiDuTemps?.id,
    displayedClassrooms.join(''),
    JSON.stringify(emploiDuTemps),
    isEmptyEDTModalOpen,
  ]);

  const handleTransferToCJClick = () => {
    setIsDateDialogOpen(true);
  };

  const onCopyToCahierJournal = (range: [Date, Date]) => {
    if (!emploiDuTemps) return;

    const from = range[0];
    return transfert({
      from,
      to: range[1],
      edtId: emploiDuTemps.id,
    }).then(() => {
      trackEvent('Transfert EDT to CJ', {
        from: format(from, 'fr-FR', isoDatePattern),
        to: format(range[1], 'fr-FR', isoDatePattern),
        edtId: emploiDuTemps.id,
      });
      navigate(
        `/annee-scolaire/${schoolyear.id}/ecole/${
          school.id
        }/cahier-journal/week/${format(
          from,
          'fr-FR',
          isoDatePatternWithSlashes,
        )}`,
      );
    });
  };

  const handleShareClick = () => {
    onOpenDrawer?.('share');
  };

  const handleSendToCMSClick = () => {
    if (emploiDuTemps) {
      setSendToCMSPayload({ edt: emploiDuTemps });
    }
  };

  const handleAddEventClick = () => {
    if (classrooms.length > 1) {
      setIsAddEventModalOpen(true);
    } else {
      openCreateSeance({
        id: uuid(),
        start: startOfMinute(setHours(date, 9)),
        end: startOfMinute(setHours(date, 10)),
        classrooms: classrooms,
      });
    }
  };

  const handleAddEventOnClassrooms = (selectedClassrooms: Classroom[]) => {
    openCreateSeance({
      id: uuid(),
      start: startOfMinute(setHours(date, 9)),
      end: startOfMinute(setHours(date, 10)),
      classrooms: selectedClassrooms,
    });
    setIsAddEventModalOpen(false);
  };

  const handleSettingsClick = () => {
    onOpenDrawer?.('settings');
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (emploiDuTemps) {
      await deleteEDT({ edtId: emploiDuTemps.id });
      navigate(
        `/annee-scolaire/${schoolyear.id}/ecole/${school.id}/emploi-du-temps`,
      );
    }
  };

  // Date dialog for transfer to CJ - calculating week ranges
  const sundayStart =
    classrooms.length > 0
      ? getMutualWorkingDays(classrooms).includes(-1)
      : false;
  const today = new Date();
  const firstDayThisWeek = startOfWeek(today, sundayStart);
  const lastDayThisWeek = endOfWeek(today, sundayStart);
  const firstDayNextWeek = addWeeks(firstDayThisWeek, 1);
  const lastDayNextWeek = endOfWeek(firstDayNextWeek, sundayStart);
  const pattern = t('common:datePattern.dd/MM');
  const firstDayThisWeekFormated = format(firstDayThisWeek, locale, pattern);
  const lastDayThisWeekFormated = format(lastDayThisWeek, locale, pattern);
  const firstDayNextWeekFormated = format(firstDayNextWeek, locale, pattern);
  const lastDayNextWeekFormated = format(lastDayNextWeek, locale, pattern);

  const actionButtons = (
    <>
      {!isReadOnly && (
        <>
          <Tooltip2 label={t('common:buttons.undo')} disabled={isPastEmpty}>
            <Button
              intent="none"
              aria-label="Revenir en arrière"
              large
              minimal
              disabled={isPastEmpty}
              onClick={undo}
              icon={ArrowUturnLeftIcon}
            />
          </Tooltip2>
          <Tooltip2 label={t('common:buttons.redo')} disabled={isFutureEmpty}>
            <Button
              large
              intent="none"
              aria-label="Revenir en avant"
              minimal
              disabled={isFutureEmpty}
              onClick={redo}
              icon={ArrowUturnRightIcon}
            />
          </Tooltip2>
        </>
      )}

      <Tooltip2 label={t('emploiDuTemps:sidebar.displayVolume')}>
        <Button
          large
          intent="none"
          id="edt-volume-horaire"
          minimal
          onClick={handleVolumeClick}
          icon={ChartPieIcon}
          aria-label={t('emploiDuTemps:sidebar.displayVolume')}
        />
      </Tooltip2>

      <Tooltip2 label={t('common:buttons.pdfPreview')}>
        <Button
          large
          intent="none"
          id="edt-preview"
          minimal
          data-testid="edt-preview"
          onClick={handlePDFClick}
          icon={PDFIcon as any}
          aria-label={t('common:buttons.pdfPreview')}
        />
      </Tooltip2>

      <Menu portal>
        <Tooltip2 label={t('emploiDuTemps:sidebar.transfertToCJ')}>
          <Menu.Button
            intent="none"
            large
            id="edt-transfer-cj"
            data-testid="edt-transfer-cj"
            minimal
            loading={isTransfering}
            icon={TransfertCJIcon as any}
            aria-label={t('emploiDuTemps:sidebar.transfertToCJ')}
          />
        </Tooltip2>
        <Menu.Items size="auto">
          <Menu.Item
            onClick={() =>
              onCopyToCahierJournal([firstDayThisWeek, lastDayThisWeek])
            }
            text={t('emploiDuTemps:sidebar.transfertThisWeek', {
              startDay: firstDayThisWeekFormated,
              endDay: lastDayThisWeekFormated,
            })}
          />
          <Menu.Item
            onClick={() =>
              onCopyToCahierJournal([firstDayNextWeek, lastDayNextWeek])
            }
            text={t('emploiDuTemps:sidebar.transfertNextWeek', {
              startDay: firstDayNextWeekFormated,
              endDay: lastDayNextWeekFormated,
            })}
          />
          <Menu.Item
            icon={CalendarDaysIcon}
            text={t('emploiDuTemps:sidebar.transfertToCustomDate')}
            onClick={handleTransferToCJClick}
          />
        </Menu.Items>
      </Menu>

      <Tooltip2 label={t('emploiDuTemps:sidebar.displaySettingsTooltip')}>
        <Button
          large
          intent="none"
          id="edt-settings"
          minimal
          onClick={handleSettingsClick}
          icon={AdjustmentsHorizontalIcon}
          aria-label={t('emploiDuTemps:sidebar.displaySettingsTooltip')}
        />
      </Tooltip2>
    </>
  );

  if (isMobile) {
    return (
      <>
        {/* Modals */}
        {isDateDialogOpen && (
          <DateDialog
            startSunday={sundayStart}
            isOpen={isDateDialogOpen}
            onSubmit={onCopyToCahierJournal}
            onClose={() => setIsDateDialogOpen(false)}
          />
        )}

        <SimpleModal
          isOpen={isEmptyEDTModalOpen}
          title={t('emploiDuTemps:sidebar.emptyEDTModal.title')}
          intent="primary"
          validateTitle={t('emploiDuTemps:sidebar.emptyEDTModal.submitLabel')}
          validateAction={downloadPDF}
          isLoading={isDownloading}
          closeModalFct={() => setIsEmptyEDTModalOpen(false)}
        >
          <div tw="text-base space-y-6">
            <div>{t('emploiDuTemps:sidebar.emptyEDTModal.description1')}</div>
            <div>{t('emploiDuTemps:sidebar.emptyEDTModal.description2')}</div>
          </div>
        </SimpleModal>

        <div
          css={[
            tw`flex items-center gap-1 overflow-x-auto`,
            { scrollbarWidth: 'none', msOverflowStyle: 'none' },
            { '&::-webkit-scrollbar': { display: 'none' } },
          ]}
        >
          {actionButtons}
          <Menu portal>
            <Menu.Button
              intent="none"
              large
              minimal
              icon={EllipsisHorizontalIcon}
              aria-label={t('common:buttons.moreOptions')}
            />
            <Menu.Items>
              <Menu.Item
                onClick={handleVolumeClick}
                icon={ChartPieIcon}
                text={t('emploiDuTemps:sidebar.displayVolume')}
              />
              <Menu.Item
                onClick={handlePDFClick}
                icon={PrinterIcon}
                text={t('common:buttons.pdfPreview')}
              />
              {!isGuest && access.hasAccessToCahierJournal && (
                <Menu.Item
                  onClick={handleTransferToCJClick}
                  icon={TransfertCJIcon as any}
                  text={t('emploiDuTemps:sidebar.transfertToCJ')}
                />
              )}
              <Menu.Item
                onClick={handleSettingsClick}
                icon={AdjustmentsHorizontalIcon}
                text={t('emploiDuTemps:sidebar.displaySettingsTooltip')}
              />
              {!isGuest && (
                <Menu.Item
                  onClick={handleShareClick}
                  icon={ShareIcon}
                  text={t('common:buttons.share')}
                />
              )}
              {!isReadOnly && (
                <Menu.Item
                  onClick={handleAddEventClick}
                  icon={PlusIcon}
                  text={t('emploiDuTemps:sidebar.addSeance')}
                />
              )}
              {shouldDisplayShare && (
                <Menu.Item
                  onClick={handleSendToCMSClick}
                  icon={ServerStackIcon}
                  text={t('common:buttons.sendToCMS')}
                />
              )}
              {!isReadOnly && !isGuest && (
                <Menu.Item
                  onClick={handleDeleteClick}
                  icon={TrashIcon}
                  intent="danger"
                  text={t('emploiDuTemps:list.tooltip.delete', 'Supprimer')}
                />
              )}
            </Menu.Items>
          </Menu>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Modals */}
      {isDateDialogOpen && (
        <DateDialog
          startSunday={sundayStart}
          isOpen={isDateDialogOpen}
          onSubmit={onCopyToCahierJournal}
          onClose={() => setIsDateDialogOpen(false)}
        />
      )}

      <SimpleModal
        isOpen={isEmptyEDTModalOpen}
        title={t('emploiDuTemps:sidebar.emptyEDTModal.title')}
        intent="primary"
        validateTitle={t('emploiDuTemps:sidebar.emptyEDTModal.submitLabel')}
        validateAction={downloadPDF}
        isLoading={isDownloading}
        closeModalFct={() => setIsEmptyEDTModalOpen(false)}
      >
        <div tw="text-base space-y-6">
          <div>{t('emploiDuTemps:sidebar.emptyEDTModal.description1')}</div>
          <div>{t('emploiDuTemps:sidebar.emptyEDTModal.description2')}</div>
        </div>
      </SimpleModal>

      <SimpleModal
        isOpen={isDeleteModalOpen}
        title={t(
          'emploiDuTemps:list.tooltip.delete',
          "Supprimer l'emploi du temps",
        )}
        intent="danger"
        validateTitle={t('common:buttons.delete')}
        validateAction={confirmDelete}
        closeModalFct={() => setIsDeleteModalOpen(false)}
      >
        <div>{t('emploiDuTemps:list.tooltip.confirmDeletion')}</div>
      </SimpleModal>

      <SimpleModal
        isOpen={isAddEventModalOpen}
        title={t('emploiDuTemps:sidebar.addSeance')}
        intent="primary"
        closeModalFct={() => setIsAddEventModalOpen(false)}
      >
        <div tw="space-y-3">
          <p>{t('emploiDuTemps:sidebar.selectClassroomForSeance')}</p>
          <div tw=" flex flex-col space-y-2">
            <Button
              small
              onClick={() => handleAddEventOnClassrooms(classrooms)}
            >
              {t('emploiDuTemps:sidebar.addSeanceOnAllClassrooms')}
            </Button>
            {classrooms.map(cl => (
              <Button
                key={cl.id}
                small
                onClick={() => handleAddEventOnClassrooms([cl])}
              >
                {t('emploiDuTemps:sidebar.addSeanceOn', {
                  classroomName: cl.name,
                })}
              </Button>
            ))}
          </div>
        </div>
      </SimpleModal>

      <div
        tw="flex items-center gap-2"
        data-testid="edt-toolbar-actions-container"
      >
        {actionButtons}
        <Menu portal>
          <Menu.Button
            intent="none"
            icon={EllipsisHorizontalIcon}
            minimal
            aria-label={t('common:buttons.moreOptions')}
          />
          <Menu.Items>
            <Menu.Item
              onClick={handleVolumeClick}
              icon={ChartPieIcon}
              text={t('emploiDuTemps:sidebar.displayVolume')}
            />
            <Menu.Item
              onClick={handlePDFClick}
              icon={PrinterIcon}
              text={t('common:buttons.pdfPreview')}
            />
            {!isGuest && access.hasAccessToCahierJournal && (
              <Menu.Item
                onClick={handleTransferToCJClick}
                icon={TransfertCJIcon as any}
                text={t('emploiDuTemps:sidebar.transfertToCJ')}
              />
            )}
            <Menu.Item
              onClick={handleSettingsClick}
              icon={AdjustmentsHorizontalIcon}
              text={t('emploiDuTemps:sidebar.displaySettingsTooltip')}
            />
            {!isGuest && (
              <Menu.Item
                onClick={handleShareClick}
                icon={ShareIcon}
                text={t('common:buttons.share')}
              />
            )}
            {!isReadOnly && (
              <Menu.Item
                onClick={handleAddEventClick}
                icon={PlusIcon}
                text={t('emploiDuTemps:sidebar.addSeance')}
              />
            )}
            {shouldDisplayShare && (
              <Menu.Item
                onClick={handleSendToCMSClick}
                icon={ServerStackIcon}
                text={t('common:buttons.sendToCMS')}
              />
            )}
            {!isReadOnly && !isGuest && (
              <Menu.Item
                onClick={handleDeleteClick}
                icon={TrashIcon}
                intent="danger"
                text={t('emploiDuTemps:list.tooltip.delete', 'Supprimer')}
              />
            )}
          </Menu.Items>
        </Menu>
      </div>
    </>
  );
}
